import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Divider,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Egg,
  Add,
  DeviceThermostat,
  Opacity,
  Timeline,
  Visibility,
  Edit,
  Delete,
  PlayArrow,
  Stop,
  Warning,
  BrokenImage,
  Remove,
  ReportProblem
} from '@mui/icons-material';
import { useData } from '../contexts/DataContext';


const HatchingIncubation = () => {
  const { incubationBatches, loadIncubationBatches } = useData();
  const [incubators, setIncubators] = useState([]);
  const [eggBatches, setEggBatches] = useState([]);
  const [temperatureHumidityLogs, setTemperatureHumidityLogs] = useState([]);
  
  // Dialog states
  const [newBatchOpen, setNewBatchOpen] = useState(false);
  const [newIncubatorOpen, setNewIncubatorOpen] = useState(false);
  const [recordTempHumidOpen, setRecordTempHumidOpen] = useState(false);
  const [viewBatchOpen, setViewBatchOpen] = useState(false);
  const [damagedEggsOpen, setDamagedEggsOpen] = useState(false);
  const [selectedBatch, setSelectedBatch] = useState(null);
  
  // Form states
  const [newBatch, setNewBatch] = useState({
    code: '',
    totalEggs: '',
    incubatorId: '',
    breed: '',
    startDate: new Date().toISOString().split('T')[0]
  });
  
  const [newIncubator, setNewIncubator] = useState({
    name: '',
    capacity: ''
  });
  
  const [tempHumidRecord, setTempHumidRecord] = useState({
    incubatorId: '',
    temperature: '',
    humidity: ''
  });

  const [damagedEggsRecord, setDamagedEggsRecord] = useState({
    batchId: '',
    damagedCount: '',
    reason: '',
    notes: ''
  });

  // Load real incubation data
  useEffect(() => {
    loadIncubationBatches();
    loadIncubators();
  }, []);

  // Load incubators from API
  const loadIncubators = async () => {
    try {
      const response = await fetch('/api/production/incubators');
      if (response.ok) {
        const data = await response.json();
        const mappedData = data.map(inc => ({ ...inc, currentEggs: parseInt(inc.current_eggs) || 0 })); setIncubators(mappedData);
      } else {
        console.error('Failed to load incubators');
      }
    } catch (error) {
      console.error('Error loading incubators:', error);
    }
  };


  // Update egg batches when real data is loaded
  useEffect(() => {
    if (incubationBatches && incubationBatches.length > 0) {
      // Initialize real incubator if not already done
      if (incubators.length === 0) {
        setIncubators([{
          id: 3,
          name: "Incubator C",
          capacity: 265,
          currentBatchId: null,
          temperature: 37.0,
          humidity: 60,
          status: "active",
          currentEggs: 0,
          daysRemaining: 10
        }]);
      }
      const realBatches = incubationBatches.map(batch => ({
        id: batch.id,
        code: batch.batch_code,
        startDate: batch.incubation_start_date?.split('T')[0],
        expectedHatchDate: batch.expected_hatch_date?.split('T')[0],
        totalEggs: parseInt(batch.total_eggs_set) || 0,
        damagedEggs: parseInt(batch.damaged_eggs) || 0,
        viableEggs: parseInt(batch.viable_eggs) || parseInt(batch.total_eggs_set) || 0,
        hatchedEggs: 0,
        hatchingSuccessRate: null,
        currentStatus: batch.status || 'incubating',
        incubatorId: 3,
        breed: "Mixed Breed"
      }));
      
      // Update with real data but keep sample data if no real data exists
      if (realBatches.length > 0) {
        setEggBatches(realBatches);
        
        // Update incubator current eggs
        const totalViableEggs = realBatches
          .filter(batch => batch.currentStatus === 'incubating')
          .reduce((sum, batch) => sum + batch.viableEggs, 0);
        
        setIncubators(prev => prev.map(inc => 
          inc.id === 3 ? { ...inc, currentEggs: totalViableEggs > 0 ? totalViableEggs : 0 } : inc
        ));
      }
    }
  }, [incubationBatches]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'success';
      case 'idle': return 'default';
      case 'maintenance': return 'warning';
      default: return 'default';
    }
  };

  const getBatchStatusColor = (status) => {
    switch (status) {
      case 'incubating': return 'primary';
      case 'hatching': return 'warning';
      case 'completed': return 'success';
      case 'failed': return 'error';
      default: return 'default';
    }
  };

  const calculateDaysRemaining = (expectedDate) => {
    const today = new Date();
    const expected = new Date(expectedDate);
    const diffTime = expected - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return Math.max(0, diffDays);
  };

  const calculateProgress = (startDate, expectedDate) => {
    const start = new Date(startDate);
    const expected = new Date(expectedDate);
    const today = new Date();
    
    const totalDays = (expected - start) / (1000 * 60 * 60 * 24);
    const elapsedDays = (today - start) / (1000 * 60 * 60 * 24);
    
    return Math.min(100, Math.max(0, (elapsedDays / totalDays) * 100));
  };

const handleStartNewBatch = async () => {
    try {
      // Prepare data for API
      const batchData = {
        batch_code: `INC-${new Date().toISOString().slice(0,10).replace(/-/g, '')}-${String(Math.floor(Math.random() * 900) + 100).padStart(3, '0')}`,
        incubation_start_date: newBatch.startDate,
        expected_hatch_date: new Date(new Date(newBatch.startDate).getTime() + 21 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        total_eggs_set: parseInt(newBatch.totalEggs),
        incubator_type: 'Automatic Incubator',
        notes: `New incubation batch - ${newBatch.breed} breed`,
        incubator_id: parseInt(newBatch.incubatorId)
      };

      // Make API call to save to database
      const response = await fetch('/api/production/incubation-batches', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(batchData)
      });

      if (!response.ok) {
        throw new Error(`Failed to create batch: ${response.statusText}`);
      }

      const result = await response.json();
      
      // Reload data from server to get updated list
      await loadIncubationBatches();
      
      // Success message
      alert(`✅ Incubation batch ${batchData.batch_code} created successfully!`);
      
    } catch (error) {
      console.error('Error creating incubation batch:', error);
      alert(`❌ Failed to create incubation batch: ${error.message}`);
      return; // Don't close dialog on error
    }
    
    // Reset form and close dialog only on success
    setNewBatch({ 
      code: '', 
      totalEggs: '', 
      incubatorId: '', 
      breed: '', 
      startDate: new Date().toISOString().split('T')[0] 
    });
    setNewBatchOpen(false);
  };

  const handleAddIncubator = async () => {
    try {
      // Make API call to save to database
      const response = await fetch('/api/production/incubators', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: newIncubator.name,
          capacity: parseInt(newIncubator.capacity)
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create incubator');
      }

      const result = await response.json();
      
      // Reload incubators from server
      await loadIncubators();
      
      // Success message
      alert(`✅ Incubator "${newIncubator.name}" created successfully!`);
      
    } catch (error) {
      console.error('Error creating incubator:', error);
      alert(`❌ Failed to create incubator: ${error.message}`);
      return; // Don't close dialog on error
    }
    
    // Reset form and close dialog only on success
    setNewIncubator({ name: '', capacity: '' });
    setNewIncubatorOpen(false);
  };

  const handleRecordTempHumidity = async () => {
    try {
      // Make API call to update incubator environment
      const response = await fetch(`/api/production/incubators/${tempHumidRecord.incubatorId}/environment`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          temperature: parseFloat(tempHumidRecord.temperature),
          humidity: parseInt(tempHumidRecord.humidity)
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update environment');
      }

      // Also add to local temperature logs for display
      const nextId = Math.max(...temperatureHumidityLogs.map(l => l.id), 0) + 1;
      const record = {
        id: nextId,
        incubatorId: parseInt(tempHumidRecord.incubatorId),
        temperature: parseFloat(tempHumidRecord.temperature),
        humidity: parseFloat(tempHumidRecord.humidity),
        date: new Date().toISOString().split('T')[0],
        recordedAt: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      };
      
      setTemperatureHumidityLogs([...temperatureHumidityLogs, record]);
      
      // Reload incubators to get updated temperature/humidity
      await loadIncubators();
      
      // Success message
      alert(`✅ Temperature/Humidity updated successfully!`);
      
    } catch (error) {
      console.error('Error updating environment:', error);
      alert(`❌ Failed to update environment: ${error.message}`);
      return; // Don't close dialog on error
    }
    
    // Reset form and close dialog only on success
    setTempHumidRecord({ incubatorId: '', temperature: '', humidity: '' });
    setRecordTempHumidOpen(false);
  };

  const handleOpenDamagedEggs = (batchId = '') => {
    setDamagedEggsRecord({ 
      batchId: batchId.toString(), 
      damagedCount: '', 
      reason: '', 
      notes: '' 
    });
    setDamagedEggsOpen(true);
  };

  const handleRecordDamagedEggs = async () => {
    try {
      const response = await fetch(`/api/production/incubation-batches/${damagedEggsRecord.batchId}/damaged-eggs`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          damaged_count: parseInt(damagedEggsRecord.damagedCount),
          reason: damagedEggsRecord.reason,
          notes: damagedEggsRecord.notes
        })
      });

      const result = await response.json();
      
      if (response.ok) {
        // Update the local egg batches data
        setEggBatches(prev => prev.map(batch => 
          batch.id === parseInt(damagedEggsRecord.batchId) 
            ? { 
                ...batch, 
                totalEggs: result.new_total,
                damagedEggs: (batch.damagedEggs || 0) + parseInt(damagedEggsRecord.damagedCount),
                viableEggs: result.new_total
              }
            : batch
        ));
        
        // Update incubator current eggs
        const updatedBatch = eggBatches.find(batch => batch.id === parseInt(damagedEggsRecord.batchId));
        if (updatedBatch) {
          setIncubators(prev => prev.map(inc => 
            inc.id === updatedBatch.incubatorId 
              ? { ...inc, currentEggs: result.new_total }
              : inc
          ));
        }

        // Refresh data
        await loadIncubationBatches();
        
        alert(`${result.message}`);
      } else {
        alert(`Error: ${result.error}`);
      }
    } catch (error) {
      console.error('Error recording damaged eggs:', error);
      alert('Failed to record damaged eggs. Please try again.');
    }
    
    setDamagedEggsRecord({ batchId: '', damagedCount: '', reason: '', notes: '' });
    setDamagedEggsOpen(false);
  };

  const handleViewBatch = (batch) => {
    setSelectedBatch(batch);
    setViewBatchOpen(true);
  };

  const handleClearLogs = () => {
    setTemperatureHumidityLogs([]);
  };

  const activeIncubators = incubators.filter(inc => inc.status === 'active').length;
  const activeBatches = eggBatches.filter(batch => batch.currentStatus === 'incubating').length;
  const totalEggsIncubating = eggBatches
    .filter(batch => batch.currentStatus === 'incubating')
    .reduce((sum, batch) => sum + batch.viableEggs, 0);

  return (
    <Box>
      <Typography variant="h5" gutterBottom>Hatching & Incubation Management</Typography>
      <Divider sx={{ mb: 3 }} />
      {/* EMERGENCY TEST - DAMAGED EGGS BUTTON */}
      <Box sx={{ mb: 3, p: 2, border: '3px solid red', backgroundColor: '#ffebee' }}>
        <Typography variant="h4" color="error" gutterBottom>
          ⚠️ EMERGENCY DAMAGE REPORTING SECTION ⚠️
        </Typography>
        <Button 
          variant="contained" 
          size="large"
          color="error"
          startIcon={<ReportProblem />}
          onClick={() => handleOpenDamagedEggs()}
          sx={{ 
            mr: 2, 
            fontWeight: 'bold', 
            fontSize: '18px',
            padding: '15px 30px',
            backgroundColor: '#d32f2f',
            '&:hover': {
              backgroundColor: '#b71c1c'
            }
          }}
        >
          🚨 REPORT DAMAGED EGGS NOW 🚨
        </Button>
        <Typography variant="body1" color="error">
          Click this button to report damaged eggs for any incubation batch
        </Typography>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>Active Incubators</Typography>
              <Typography variant="h4" color="primary">{activeIncubators}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>Active Batches</Typography>
              <Typography variant="h4" color="secondary">{activeBatches}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>Eggs Incubating</Typography>
              <Typography variant="h4" color="success.main">{totalEggsIncubating}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>Avg Success Rate</Typography>
              <Typography variant="h4" color="warning.main">
                {eggBatches.length > 0 ? (eggBatches
                  .filter(b => b.hatchingSuccessRate !== null)
                  .reduce((sum, b) => sum + b.hatchingSuccessRate, 0) / 
                  eggBatches.filter(b => b.hatchingSuccessRate !== null).length || 0
                ).toFixed(1) : '0.0'}%
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Action Buttons */}
      <Box sx={{ mb: 3 }}>
        <Button 
          variant="contained" 
          startIcon={<Add />}
          onClick={() => setNewBatchOpen(true)}
          sx={{ mr: 2 }}
        >
          Start New Incubation
        </Button>
        <Button 
          variant="outlined" 
          startIcon={<DeviceThermostat />}
          onClick={() => setRecordTempHumidOpen(true)}
          sx={{ mr: 2 }}
        >
          Record Temp/Humidity
        </Button>
        <Button 
          variant="contained" 
          color="error"
          startIcon={<ReportProblem />}
          onClick={() => handleOpenDamagedEggs()}
          sx={{ mr: 2, fontWeight: 'bold' }}
        >
          🚨 REPORT DAMAGED EGGS 🚨
        </Button>
        <Button 
          variant="outlined" 
          startIcon={<Add />}
          onClick={() => setNewIncubatorOpen(true)}
          sx={{ mr: 2 }}
        >
          Add Incubator
        </Button>
        <Button 
          variant="outlined" 
          color="warning"
          startIcon={<Delete />}
          onClick={handleClearLogs}
          disabled={temperatureHumidityLogs.length === 0}
        >
          Clear Logs
        </Button>
      </Box>

      <Grid container spacing={2} alignItems="stretch" sx={{ mt: 2 }}>
        {/* Incubators Status */}
        <Grid item xs={12} md={6} sx={{ display: 'flex' }}>
          <Card sx={{ width: '100%' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Incubators Status</Typography>
              {incubators.length === 0 ? (
                <Alert severity="info">No incubators available. Add a new incubator to get started.</Alert>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Incubator</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Temp°C</TableCell>
                        <TableCell>Humidity%</TableCell>
                        <TableCell>Eggs</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {incubators.map((incubator) => (
                        <TableRow key={incubator.id}>
                          <TableCell>{incubator.name}</TableCell>
                          <TableCell>
                            <Chip 
                              label={incubator.status} 
                              color={getStatusColor(incubator.status)}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <DeviceThermostat fontSize="small" sx={{ mr: 0.5 }} />
                              {incubator.temperature}
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Opacity fontSize="small" sx={{ mr: 0.5 }} />
                              {incubator.humidity}
                            </Box>
                          </TableCell>
                          <TableCell>{incubator.currentEggs}/{incubator.capacity}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Egg Batches */}
        <Grid item xs={12} md={6} sx={{ display: 'flex' }}>
          <Card sx={{ width: '100%' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Egg Batches</Typography>
              {eggBatches.length === 0 ? (
                <Alert severity="info">No active egg batches. Start a new incubation batch to begin.</Alert>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Batch Code</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Eggs (Viable/Total)</TableCell>
                        <TableCell>Progress</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {eggBatches.map((batch) => (
                        <TableRow key={batch.id}>
                          <TableCell>{batch.code}</TableCell>
                          <TableCell>
                            <Chip 
                              label={batch.currentStatus} 
                              color={getBatchStatusColor(batch.currentStatus)}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              <strong>{batch.viableEggs}/{batch.totalEggs}</strong>
                              {batch.damagedEggs > 0 && (
                                <Typography component="span" variant="caption" color="error" sx={{ ml: 1 }}>
                                  ({batch.damagedEggs} damaged)
                                </Typography>
                              )}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ width: '100%' }}>
                              <LinearProgress 
                                variant="determinate" 
                                value={calculateProgress(batch.startDate, batch.expectedHatchDate)}
                                sx={{ mb: 0.5 }}
                              />
                              <Typography variant="caption">
                                {calculateDaysRemaining(batch.expectedHatchDate)} days left
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                              <Tooltip title="View Details">
                                <IconButton size="small" onClick={() => handleViewBatch(batch)}>
                                  <Visibility fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              {true && (
                                <Tooltip title="Report Damage">
                                  <IconButton 
                                    size="small" 
                                    color="error"
                                    onClick={() => handleOpenDamagedEggs(batch.id)}
                                  >
                                    <BrokenImage fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Recent Temperature/Humidity Logs */}
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">Recent Temperature & Humidity Logs</Typography>
            {temperatureHumidityLogs.length > 0 && (
              <Button 
                variant="outlined" 
                color="warning" 
                size="small"
                startIcon={<Delete />}
                onClick={handleClearLogs}
              >
                Clear All Logs
              </Button>
            )}
          </Box>
          {temperatureHumidityLogs.length === 0 ? (
            <Alert severity="info">No temperature/humidity logs. Record some data to see it here.</Alert>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Incubator</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Time</TableCell>
                    <TableCell>Temperature (°C)</TableCell>
                    <TableCell>Humidity (%)</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {temperatureHumidityLogs.slice(-10).map((log) => {
                    const incubator = incubators.find(inc => inc.id === log.incubatorId);
                    const tempOk = log.temperature >= 37.0 && log.temperature <= 38.0;
                    const humidityOk = log.humidity >= 50 && log.humidity <= 60;
                    
                    return (
                      <TableRow key={log.id}>
                        <TableCell>{incubator?.name}</TableCell>
                        <TableCell>{log.date}</TableCell>
                        <TableCell>{log.recordedAt}</TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            {log.temperature}
                            {!tempOk && <Warning color="warning" sx={{ ml: 1 }} fontSize="small" />}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            {log.humidity}
                            {!humidityOk && <Warning color="warning" sx={{ ml: 1 }} fontSize="small" />}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={tempOk && humidityOk ? "Optimal" : "Warning"} 
                            color={tempOk && humidityOk ? "success" : "warning"}
                            size="small"
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* 🚨 REPORT DAMAGED EGGS 🚨 Dialog */}
      <Dialog open={damagedEggsOpen} onClose={() => setDamagedEggsOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ 
          background: 'linear-gradient(45deg, #F44336 30%, #FF5722 90%)', 
          color: 'white',
          textAlign: 'center',
          py: 3
        }}>
          <Typography variant="h5" component="div" sx={{ fontWeight: 'bold' }}>
            🚨 REPORT DAMAGED EGGS 🚨
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Select Incubation Batch</InputLabel>
                <Select
                  value={damagedEggsRecord.batchId}
                  onChange={(e) => setDamagedEggsRecord({...damagedEggsRecord, batchId: e.target.value})}
                  label="Select Incubation Batch"
                >
                  {eggBatches.filter(batch => batch.currentStatus === 'incubating').map((batch) => (
                    <MenuItem key={batch.id} value={batch.id}>
                      <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                        <Typography>
                          {batch.code}
                        </Typography>
                        <Typography variant="body2" color="textSecondary" sx={{ ml: 2 }}>
                          ({batch.viableEggs} viable eggs)
                        </Typography>
                        {batch.damagedEggs > 0 && (
                          <Typography variant="caption" color="error" sx={{ ml: 1 }}>
                            [{batch.damagedEggs} already damaged]
                          </Typography>
                        )}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Number of Damaged Eggs"
                type="number"
                value={damagedEggsRecord.damagedCount}
                onChange={(e) => setDamagedEggsRecord({...damagedEggsRecord, damagedCount: e.target.value})}
                inputProps={{ min: 1 }}
                helperText="Enter the number of eggs that are damaged or need to be removed"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Reason for Damage</InputLabel>
                <Select
                  value={damagedEggsRecord.reason}
                  onChange={(e) => setDamagedEggsRecord({...damagedEggsRecord, reason: e.target.value})}
                  label="Reason for Damage"
                >
                  <MenuItem value="Cracked shells">Cracked shells</MenuItem>
                  <MenuItem value="Contamination">Contamination</MenuItem>
                  <MenuItem value="Overheating">Overheating</MenuItem>
                  <MenuItem value="Power failure">Power failure</MenuItem>
                  <MenuItem value="Tray damage">Tray damage</MenuItem>
                  <MenuItem value="Candling - clear">Candling - clear (infertile)</MenuItem>
                  <MenuItem value="Candling - dead embryo">Candling - dead embryo</MenuItem>
                  <MenuItem value="Other">Other</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Additional Notes"
                multiline
                rows={3}
                value={damagedEggsRecord.notes}
                onChange={(e) => setDamagedEggsRecord({...damagedEggsRecord, notes: e.target.value})}
                placeholder="Describe the damage incident, location (tray number), time discovered, actions taken, etc."
              />
            </Grid>
          </Grid>
          <Alert severity="error" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>Warning:</strong> This action will permanently remove the damaged eggs from the incubation count. 
              This cannot be undone. Please ensure the count and reason are accurate.
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions sx={{ p: 3, bgcolor: '#ffebee' }}>
          <Button 
            onClick={() => setDamagedEggsOpen(false)}
            sx={{ mr: 1 }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleRecordDamagedEggs}
            variant="contained"
            disabled={!damagedEggsRecord.batchId || !damagedEggsRecord.damagedCount || !damagedEggsRecord.reason}
            sx={{
              background: 'linear-gradient(45deg, #F44336 30%, #FF5722 90%)',
              color: 'white',
              fontWeight: 'bold',
              px: 3,
              '&:hover': {
                background: 'linear-gradient(45deg, #D32F2F 30%, #E64A19 90%)',
              }
            }}
          >
            🚨 Record Damage
          </Button>
        </DialogActions>
      </Dialog>

      {/* New Batch Dialog */}
      <Dialog open={newBatchOpen} onClose={() => setNewBatchOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ 
          background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)', 
          color: 'white',
          textAlign: 'center',
          py: 3
        }}>
          <Typography variant="h5" component="div" sx={{ fontWeight: 'bold' }}>
            🥚 Start New Incubation Batch
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Number of Eggs"
                type="number"
                value={newBatch.totalEggs}
                onChange={(e) => setNewBatch({...newBatch, totalEggs: e.target.value})}
                inputProps={{ min: 1, max: 265 }}
                helperText="Enter the number of eggs to incubate (1-265)"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&:hover fieldset': { borderColor: '#2196F3' },
                    '&.Mui-focused fieldset': { borderColor: '#2196F3' }
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel sx={{ color: '#2196F3' }}>Select Incubator</InputLabel>
                <Select
                  value={newBatch.incubatorId}
                  onChange={(e) => setNewBatch({...newBatch, incubatorId: e.target.value})}
                  label="Select Incubator"
                  sx={{
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#2196F3' },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#2196F3' }
                  }}
                >
                  {incubators.map((incubator) => {
                    const availableSpace = incubator.capacity - incubator.currentEggs;
                    return (
                      <MenuItem key={incubator.id} value={incubator.id}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                          <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                            {incubator.name}
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            Available space: {availableSpace}/{incubator.capacity} eggs
                          </Typography>
                          <Typography variant="caption" color={incubator.status === 'active' ? 'success.main' : 'text.secondary'}>
                            Status: {incubator.status.toUpperCase()}
                          </Typography>
                        </Box>
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Breed"
                value={newBatch.breed}
                onChange={(e) => setNewBatch({...newBatch, breed: e.target.value})}
                placeholder="e.g., Rhode Island Red, Leghorn, Mixed"
                helperText="Enter the chicken breed"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&:hover fieldset': { borderColor: '#2196F3' },
                    '&.Mui-focused fieldset': { borderColor: '#2196F3' }
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Start Date"
                type="date"
                value={newBatch.startDate}
                onChange={(e) => setNewBatch({...newBatch, startDate: e.target.value})}
                InputLabelProps={{ shrink: true }}
                helperText="Incubation start date"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&:hover fieldset': { borderColor: '#2196F3' },
                    '&.Mui-focused fieldset': { borderColor: '#2196F3' }
                  }
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ 
                p: 2, 
                bgcolor: '#f5f5f5', 
                borderRadius: 2,
                border: '1px solid #e0e0e0'
              }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  📋 <strong>Incubation Information:</strong>
                </Typography>
                <Typography variant="caption" display="block" sx={{ mb: 0.5 }}>
                  • Optimal incubation period: 21 days
                </Typography>
                <Typography variant="caption" display="block" sx={{ mb: 0.5 }}>
                  • Expected hatch date: {newBatch.startDate ? 
                    new Date(new Date(newBatch.startDate).getTime() + 21 * 24 * 60 * 60 * 1000)
                      .toLocaleDateString() : 'Select start date'}
                </Typography>
                <Typography variant="caption" display="block">
                  • Temperature: 37.0-38.0°C | Humidity: 50-60%
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3, bgcolor: '#f9f9f9' }}>
          <Button 
            onClick={() => setNewBatchOpen(false)}
            sx={{ mr: 1 }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleStartNewBatch}
            variant="contained"
            disabled={!newBatch.totalEggs || !newBatch.incubatorId || !newBatch.breed}
            sx={{
              background: 'linear-gradient(45deg, #4CAF50 30%, #8BC34A 90%)',
              color: 'white',
              fontWeight: 'bold',
              px: 3,
              '&:hover': {
                background: 'linear-gradient(45deg, #45a049 30%, #7CB342 90%)',
              }
            }}
          >
            🚀 Start Incubation
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add New Incubator Dialog */}
      <Dialog open={newIncubatorOpen} onClose={() => setNewIncubatorOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ 
          background: 'linear-gradient(45deg, #9C27B0 30%, #E1BEE7 90%)', 
          color: 'white',
          textAlign: 'center',
          py: 3
        }}>
          <Typography variant="h5" component="div" sx={{ fontWeight: 'bold' }}>
            🏭 Add New Incubator
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Incubator Name"
                value={newIncubator.name}
                onChange={(e) => setNewIncubator({...newIncubator, name: e.target.value})}
                placeholder="e.g., Incubator D, Main Unit, Backup"
                helperText="Enter a unique name for the incubator"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&:hover fieldset': { borderColor: '#9C27B0' },
                    '&.Mui-focused fieldset': { borderColor: '#9C27B0' }
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Capacity (number of eggs)"
                type="number"
                value={newIncubator.capacity}
                onChange={(e) => setNewIncubator({...newIncubator, capacity: e.target.value})}
                inputProps={{ min: 10, max: 1000 }}
                helperText="Maximum number of eggs it can hold"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&:hover fieldset': { borderColor: '#9C27B0' },
                    '&.Mui-focused fieldset': { borderColor: '#9C27B0' }
                  }
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ 
                p: 2, 
                bgcolor: '#f3e5f5', 
                borderRadius: 2,
                border: '1px solid #ce93d8'
              }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  🏭 <strong>Incubator Setup Information:</strong>
                </Typography>
                <Typography variant="caption" display="block" sx={{ mb: 0.5 }}>
                  • Ensure proper ventilation and stable power supply
                </Typography>
                <Typography variant="caption" display="block" sx={{ mb: 0.5 }}>
                  • Default settings: 37°C temperature, 55% humidity
                </Typography>
                <Typography variant="caption" display="block">
                  • Regular calibration recommended for optimal performance
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3, bgcolor: '#f3e5f5' }}>
          <Button 
            onClick={() => setNewIncubatorOpen(false)}
            sx={{ mr: 1 }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleAddIncubator}
            variant="contained"
            disabled={!newIncubator.name || !newIncubator.capacity}
            sx={{
              background: 'linear-gradient(45deg, #9C27B0 30%, #BA68C8 90%)',
              color: 'white',
              fontWeight: 'bold',
              px: 3,
              '&:hover': {
                background: 'linear-gradient(45deg, #7B1FA2 30%, #AB47BC 90%)',
              }
            }}
          >
            🏭 Add Incubator
          </Button>
        </DialogActions>
      </Dialog>

      {/* Record Temperature/Humidity Dialog */}
      <Dialog open={recordTempHumidOpen} onClose={() => setRecordTempHumidOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ 
          background: 'linear-gradient(45deg, #FF9800 30%, #FFB74D 90%)', 
          color: 'white',
          textAlign: 'center',
          py: 3
        }}>
          <Typography variant="h5" component="div" sx={{ fontWeight: 'bold' }}>
            🌡️ Record Temperature & Humidity
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel sx={{ color: '#FF9800' }}>Select Incubator</InputLabel>
                <Select
                  value={tempHumidRecord.incubatorId}
                  onChange={(e) => setTempHumidRecord({...tempHumidRecord, incubatorId: e.target.value})}
                  label="Select Incubator"
                  sx={{
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#FF9800' },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#FF9800' }
                  }}
                >
                  {incubators.map((incubator) => (
                    <MenuItem key={incubator.id} value={incubator.id}>
                      <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                        <Typography variant="body1" sx={{ fontWeight: 'bold', mr: 2 }}>
                          {incubator.name}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          Current: {incubator.temperature}°C, {incubator.humidity}%
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Temperature (°C)"
                type="number"
                step="0.1"
                value={tempHumidRecord.temperature}
                onChange={(e) => setTempHumidRecord({...tempHumidRecord, temperature: e.target.value})}
                inputProps={{ min: 30, max: 45, step: 0.1 }}
                helperText="Optimal range: 37.0-38.0°C"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&:hover fieldset': { borderColor: '#FF9800' },
                    '&.Mui-focused fieldset': { borderColor: '#FF9800' }
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Humidity (%)"
                type="number"
                value={tempHumidRecord.humidity}
                onChange={(e) => setTempHumidRecord({...tempHumidRecord, humidity: e.target.value})}
                inputProps={{ min: 30, max: 80 }}
                helperText="Optimal range: 50-60%"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&:hover fieldset': { borderColor: '#FF9800' },
                    '&.Mui-focused fieldset': { borderColor: '#FF9800' }
                  }
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ 
                p: 2, 
                bgcolor: '#fff3e0', 
                borderRadius: 2,
                border: '1px solid #ffcc02'
              }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  📊 <strong>Environmental Guidelines:</strong>
                </Typography>
                <Typography variant="caption" display="block" sx={{ mb: 0.5 }}>
                  🌡️ Temperature: 37.0-38.0°C (98.6-100.4°F) - Critical for proper development
                </Typography>
                <Typography variant="caption" display="block" sx={{ mb: 0.5 }}>
                  💧 Humidity: 50-60% (first 18 days), 65-70% (last 3 days)
                </Typography>
                <Typography variant="caption" display="block">
                  ⏰ Record readings at least twice daily for accurate monitoring
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3, bgcolor: '#fff3e0' }}>
          <Button 
            onClick={() => setRecordTempHumidOpen(false)}
            sx={{ mr: 1 }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleRecordTempHumidity}
            variant="contained"
            disabled={!tempHumidRecord.incubatorId || !tempHumidRecord.temperature || !tempHumidRecord.humidity}
            sx={{
              background: 'linear-gradient(45deg, #FF9800 30%, #FFB74D 90%)',
              color: 'white',
              fontWeight: 'bold',
              px: 3,
              '&:hover': {
                background: 'linear-gradient(45deg, #F57C00 30%, #FF8A65 90%)',
              }
            }}
          >
            📝 Record Data
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Batch Details Dialog */}
      <Dialog open={viewBatchOpen} onClose={() => setViewBatchOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ 
          background: 'linear-gradient(45deg, #607D8B 30%, #90A4AE 90%)', 
          color: 'white',
          textAlign: 'center',
          py: 3
        }}>
          <Typography variant="h5" component="div" sx={{ fontWeight: 'bold' }}>
            📊 Batch Details: {selectedBatch?.code}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          {selectedBatch && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={6}>
                <Typography><strong>Breed:</strong> {selectedBatch.breed}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography><strong>Total Eggs:</strong> {selectedBatch.totalEggs}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography><strong>Viable Eggs:</strong> {selectedBatch.viableEggs || selectedBatch.totalEggs}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography><strong>Damaged Eggs:</strong> {selectedBatch.damagedEggs || 0}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography><strong>Start Date:</strong> {selectedBatch.startDate}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography><strong>Expected Hatch:</strong> {selectedBatch.expectedHatchDate}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography><strong>Status:</strong> 
                  <Chip 
                    label={selectedBatch.currentStatus} 
                    color={getBatchStatusColor(selectedBatch.currentStatus)}
                    size="small"
                    sx={{ ml: 1 }}
                  />
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography><strong>Success Rate:</strong> {selectedBatch.hatchingSuccessRate ? `${selectedBatch.hatchingSuccessRate}%` : 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography><strong>Progress:</strong></Typography>
                <LinearProgress 
                  variant="determinate" 
                  value={calculateProgress(selectedBatch.startDate, selectedBatch.expectedHatchDate)}
                  sx={{ mt: 1, mb: 1 }}
                />
                <Typography variant="body2">
                  {calculateDaysRemaining(selectedBatch.expectedHatchDate)} days remaining
                </Typography>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, bgcolor: '#eceff1' }}>
          <Button 
            onClick={() => setViewBatchOpen(false)}
            variant="contained"
            sx={{
              background: 'linear-gradient(45deg, #607D8B 30%, #90A4AE 90%)',
              color: 'white',
              fontWeight: 'bold',
              px: 3,
              '&:hover': {
                background: 'linear-gradient(45deg, #455A64 30%, #78909C 90%)',
              }
            }}
          >
            ✓ Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default HatchingIncubation;
